import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Hiring v3 / Ticket I3 — Referral pipeline admin.
// Owner sees the full pipeline (applied / hired / pending_90 / ready_for_payout
// / paid / forfeited) and can approve payouts or forfeit referrals. The
// state machine itself runs nightly via a server-side cron.
export function HiringSettingsReferrals({ currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const isOwner = /^(owner|admin|administrator)$/i.test(currentUser.role || '');

  async function load() {
    setLoading(true);
    try {
      const r = await api('GET', '/api/admin/referrals', null, headers);
      setRefs(Array.isArray(r?.referrals) ? r.referrals : []);
    } catch (_) { setRefs([]); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function pay(id) {
    const v = window.prompt('Payout amount (USD)?', '100.00');
    if (!v) return;
    const amt = Number(v);
    if (!Number.isFinite(amt) || amt <= 0) { alert('Bad amount.'); return; }
    setSaving(id);
    try {
      await api('PUT', `/api/admin/referrals/${id}`, { action: 'pay', payout_amount: amt }, headers);
      await load();
    } catch (e) { alert(e.message || String(e)); }
    finally { setSaving(null); }
  }
  async function forfeit(id) {
    const reason = window.prompt('Why forfeit this referral?', '');
    if (reason === null) return;
    setSaving(id);
    try {
      await api('PUT', `/api/admin/referrals/${id}`, { action: 'forfeit', forfeited_reason: reason }, headers);
      await load();
    } catch (e) { alert(e.message || String(e)); }
    finally { setSaving(null); }
  }

  const stateColor = {
    applied:           { bg: '#fafafa', fg: '#666',    bd: '#e0e0e0' },
    hired:             { bg: '#e6f4ea', fg: '#1e7d3a', bd: '#c8e2c8' },
    pending_90:        { bg: '#fff8e6', fg: '#7a5b1a', bd: '#f0d9a3' },
    ready_for_payout:  { bg: '#fef9ee', fg: '#8e6c33', bd: '#f5e6c8' },
    paid:              { bg: '#e8f5fe', fg: '#1565c0', bd: '#b3d6f5' },
    forfeited:         { bg: '#fef2f2', fg: '#a13030', bd: '#fecaca' },
  };

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="text-base font-semibold mb-1">Referral pipeline</div>
      <div className="text-sm text-gray-500 mb-4">
        Auto-tracks candidates referred by an existing teammate. State advances
        nightly: hired → pending 90 → ready for payout once 90 days past hire.
        {isOwner ? ' Tap Pay to approve a payout once a referral is ready.' : ' Owner approves payouts.'}
      </div>
      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : refs.length === 0 ? (
        <div className="text-sm text-gray-500">No referrals yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {refs.map(r => {
            const c = stateColor[r.state] || stateColor.applied;
            return (
              <div key={r.id} style={{ border: `1px solid ${c.bd}`, borderRadius: 8, padding: 10,
                                        background: c.bg, fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                              flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <strong>{r.first_name} {r.last_name}</strong>
                    <span style={{ color: '#666' }}> · referred by </span>
                    <strong>{r.referrer_name || r.referrer_name_raw || '—'}</strong>
                    {r.referrer_user_id == null && r.referrer_name_raw && (
                      <span style={{ marginLeft: 6, fontSize: 10, color: '#a13030',
                                      background: '#fef2f2', padding: '1px 6px', borderRadius: 4 }}>
                        unconfirmed
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 10, padding: '2px 10px', borderRadius: 99,
                                  background: '#fff', color: c.fg, border: `1px solid ${c.bd}`,
                                  fontWeight: 600, letterSpacing: '0.04em' }}>
                    {r.state.replace(/_/g, ' ')}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                  {r.hired_name && <>Hired as {r.hired_name}{r.hire_date ? ` on ${new Date(r.hire_date).toLocaleDateString()}` : ''}</>}
                  {r.payout_amount && <> · paid ${Number(r.payout_amount).toFixed(2)}</>}
                  {r.forfeited_reason && <> · forfeited: {r.forfeited_reason}</>}
                </div>
                {isOwner && r.state === 'ready_for_payout' && (
                  <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                    <button onClick={() => pay(r.id)} disabled={saving === r.id}
                      style={{ fontSize: 12, padding: '4px 12px', border: 0, borderRadius: 6,
                                background: 'var(--gold)', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
                      Approve payout
                    </button>
                    <button onClick={() => forfeit(r.id)} disabled={saving === r.id}
                      style={{ fontSize: 12, padding: '4px 12px', border: '1px solid #fecaca',
                                borderRadius: 6, background: '#fff', color: '#a13030', cursor: 'pointer' }}>
                      Forfeit
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
